import React, { useEffect, useState } from "react";
import "./Listing.css";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";

const SHORT_TERM_PREFIX = "Stay:";

const ShortTermListing = () => {
  const [shortTermListings, setShortTermListings] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const startTime = Date.now();

    const finishLoading = () => {
      const elapsed = Date.now() - startTime;
      const remaining = 2000 - elapsed;

      if (remaining > 0) {
        setTimeout(() => setLoading(false), remaining);
      } else {
        setLoading(false);
      }
    };

    const fetchListings = async () => {
      try {
        const response = await fetch(
          "https://t3yaaqetsktr267ko4xucsactu0wdogd.lambda-url.us-east-2.on.aws/",
          {
            headers: {
              "x-custom-key": "crazySecretKey82183b48921$@^JNF@!",
            },
          }
        );

        if (!response.ok) {
          throw new Error(`Listings request failed: ${response.status}`);
        }

        const data = await response.json();

        let allListings = [];

        if (Array.isArray(data)) {
          allListings = data;
        } else if (Array.isArray(data.listings)) {
          allListings = data.listings;
        }

        const onlyShortTerm = allListings.filter((listing) => {
          const unitDescription = listing.Unit?.Description || "";
          const propertyDescription =
            listing.Property?.StructureDescription || "";

          return (
            unitDescription.startsWith(SHORT_TERM_PREFIX) ||
            propertyDescription.startsWith(SHORT_TERM_PREFIX)
          );
        });

        setShortTermListings([...onlyShortTerm].reverse());
      } catch (error) {
        console.error("Error fetching short-term listings:", error);
        setShortTermListings([]);
      } finally {
        finishLoading();
      }
    };

    fetchListings();
  }, []);

  const cleanDescription = (description = "") => {
    const escapedPrefix = SHORT_TERM_PREFIX.replace(
      /[.*+?^${}()|[\]\\]/g,
      "\\$&"
    );

    return (
      description.replace(new RegExp(`^${escapedPrefix}`), "").trim() || "N/A"
    );
  };

  const getListingId = (listing) => {
    const rentalUrl = listing.RentalApplicationUrl || "";
    const queryString = rentalUrl.split("?")[1] || "";
    const urlParams = new URLSearchParams(queryString);

    return urlParams.get("listingId");
  };

  const getDescription = (unit, property) => {
    const unitDescription = unit.Description || "";
    const propertyDescription = property.StructureDescription || "";

    if (unitDescription.startsWith(SHORT_TERM_PREFIX)) {
      return cleanDescription(unitDescription);
    }

    if (propertyDescription.startsWith(SHORT_TERM_PREFIX)) {
      return cleanDescription(propertyDescription);
    }

    return "N/A";
  };

  if (loading) {
    return (
      <div className="loading-container">
        <Helmet>
          <title>Short-Term Rentals | Harrisburg, PA</title>
          <meta
            name="description"
            content="Short-term rental properties available in the Harrisburg, Pennsylvania area."
          />
          <link
            rel="canonical"
            href="https://www.deltapmg.com/short-term-listings"
          />
          <meta
            property="og:title"
            content="Short-Term Rentals | Harrisburg, PA"
          />
          <meta
            property="og:description"
            content="Short-term rental properties available in the Harrisburg, Pennsylvania area."
          />
          <meta
            property="og:url"
            content="https://www.deltapmg.com/short-term-listings"
          />
          <meta property="og:type" content="website" />
        </Helmet>

        <div className="loading-content">
          <div className="loading-spinner-wrapper">
            <div className="loading-spinner"></div>
            <div className="loading-pulse"></div>
          </div>

          <h2 className="loading-title">Loading Listings...</h2>
          <p className="loading-subtitle">
            Fetching available short-term properties
          </p>

          <div className="loading-dots">
            <span className="dot"></span>
            <span className="dot"></span>
            <span className="dot"></span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="listing-container">
      <Helmet>
        <title>Short-Term Rentals | Harrisburg, PA</title>
        <meta
          name="description"
          content="Short-term rental properties available in the Harrisburg, Pennsylvania area."
        />
        <link
          rel="canonical"
          href="https://www.deltapmg.com/short-term-listings"
        />
        <meta
          property="og:title"
          content="Short-Term Rentals | Harrisburg, PA"
        />
        <meta
          property="og:description"
          content="Short-term rental properties available in the Harrisburg, Pennsylvania area."
        />
        <meta
          property="og:url"
          content="https://www.deltapmg.com/short-term-listings"
        />
        <meta property="og:type" content="website" />
      </Helmet>

      {shortTermListings.length === 0 ? (
        <>
          <p className="no-listings-1">
            No Short-Term Rentals Are Currently Available
          </p>

          <div className="listing-preference-cta">
            <a
              href="/listing-preferences"
              className="listing-preference-btn"
            >
              Submit Rental Preferences
            </a>
          </div>
        </>
      ) : (
        <>
          <div className="listing-title-wrapper">
            <h1 className="listing-title-1">Short-Term Listings</h1>
          </div>

          {shortTermListings.map((listing, index) => {
            const unit = listing.Unit || {};
            const property = listing.Property || {};

            const image =
              unit.Files?.find((file) => file.Type === "Image")?.Url ||
              property.Files?.find((file) => file.Type === "Image")?.Url ||
              "https://via.placeholder.com/600x400?text=No+Image";

            const addressLine =
              unit.Address?.AddressLine1 ||
              property.Address?.AddressLine1 ||
              "";

            const city =
              unit.Address?.City || property.Address?.City || "";

            const state =
              unit.Address?.State || property.Address?.State || "";

            const postalCode =
              unit.Address?.PostalCode ||
              property.Address?.PostalCode ||
              "";

            const address = `${addressLine}, ${city}, ${state} ${postalCode}`;
            const listingId = getListingId(listing);

            return (
              <Link
                key={listingId || index}
                to={`/short-term-listings/${listingId}`}
                state={{ listing }}
                className="listing-card-link"
              >
                <div className="listing-card" style={{ cursor: "pointer" }}>
                  <div className="listing-image">
                    <img src={image} alt={address || "Short-term property"} />
                  </div>

                  <div className="listing-details">
                    <h3>{address}</h3>

                    <p>
                      <strong>Sqft:</strong> {unit.UnitSize || "N/A"}
                    </p>

                    <p className="listing-description">
                      <strong>Description:</strong>{" "}
                      {getDescription(unit, property)}
                    </p>

                    <p>
                      <strong>Price:</strong>{" "}
                      {listing.Rent ? `$${listing.Rent}` : "N/A"}
                    </p>

                    <p>
                      <strong>Available Date:</strong>{" "}
                      {listing.AvailableDate || "N/A"}
                    </p>
                  </div>
                </div>
              </Link>
            );
          })}

          <div className="listing-preference-cta">
            <h2>Didn&apos;t find what you are looking for?</h2>

            <a
              href="/listing-preferences"
              className="listing-preference-btn"
            >
              Submit Rental Preferences
            </a>
          </div>
        </>
      )}
    </div>
  );
};

export default ShortTermListing;